import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { env } from '@/utilities/env.ts'
import {
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  LogLevel,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser'
import type { User } from '@/entities/User'

const redirectRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
  prompt: 'select_account',
}

const msalConfig: Configuration = {
  auth: {
    clientId: env('VITE_AZURE_AUTH_CLIENT_ID'),
    authority: `https://login.microsoftonline.com/${env('VITE_AZURE_AUTH_TENANT_ID')}/v2.0`,
    redirectUri: env('VITE_AZURE_AUTH_REDIRECT_URI') || window.location.origin,
    postLogoutRedirectUri: env('VITE_AZURE_AUTH_REDIRECT_URI') || window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) return
        if (level === LogLevel.Error) {
          console.error('MSAL Error:', message)
        }
      },
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
}

export const useAuthStore = defineStore('auth', () => {
  const msalInstance = ref(new PublicClientApplication(msalConfig))
  const user = ref<User | null>(null)
  const isInitialized = ref(false)
  const isAuthenticated = ref(false)
  const isAuthEnabled = computed(
    () =>
      env('VITE_ENABLE_MSAL_AUTH') === 'true' &&
      !!env('VITE_AZURE_AUTH_CLIENT_ID') &&
      !!env('VITE_AZURE_AUTH_TENANT_ID'),
  )

  const apiScopes = computed(() => {
    if (!isAuthEnabled.value || !env('VITE_AZURE_AUTH_API_CLIENT_ID')) {
      return []
    }
    return [`api://${env('VITE_AZURE_AUTH_API_CLIENT_ID')}/access_as_user`]
  })

  const tokenRefreshPromise = ref<Promise<string | null> | null>(null)
  const loginPromise = ref<Promise<void> | null>(null)

  /**
   * Load the active account from MSAL and set it as the active account in the MSAL instance and store.
   *
   * @param result - The authentication result from MSAL after handling the redirect promise.
   * @return A promise that resolves when the active account is loaded and set.
   */
  async function loadActiveAccount(result: AuthenticationResult | null) {
    console.log(result)
    if (result?.account) {
      msalInstance.value.setActiveAccount(result.account)
    } else {
      const accounts = msalInstance.value.getAllAccounts()
      if (accounts.length > 0) {
        msalInstance.value.setActiveAccount(accounts[0])
      }
    }

    const account = msalInstance.value.getActiveAccount()

    if (!account) {
      console.log('!!!!!!!!!!!!!!!!!!!')
      return null
    }

    user.value = {
      initials: account?.username.split('@')[0].toUpperCase() || null,
      username: account?.username || null,
      name: account?.name || null,
    }

    isAuthenticated.value = true
  }

  /**
   * Initializes the MSAL instance and checks for an active account.
   *
   * If an active account is found, it sets the user state accordingly. If authentication is not enabled, it sets a
   * default user.
   *
   * @returns A promise that resolves when the initialization is complete.
   */
  async function initialize() {
    await msalInstance.value.initialize()

    try {
      const result = await msalInstance.value.handleRedirectPromise()
      await loadActiveAccount(result)
    } catch (err) {
      throw err
    }

    isInitialized.value = true
  }

  /**
   * Logs in the user using MSAL's redirect method.
   */
  async function login() {
    if (loginPromise.value) {
      return loginPromise.value
    }

    loginPromise.value = performLogin()

    try {
      return await loginPromise.value
    } finally {
      loginPromise.value = null
    }
  }

  async function performLogin() {
    try {
      await msalInstance.value.loginRedirect(redirectRequest)
    } catch (err) {
      throw err
    }
  }

  /**
   * Acquires an access token for the API using MSAL's acquireTokenSilent method.
   */
  async function acquireToken(): Promise<string | null> {
    if (tokenRefreshPromise.value) {
      return tokenRefreshPromise.value
    }

    const account = msalInstance.value.getActiveAccount()

    if (!account) {
      await login()
    }

    tokenRefreshPromise.value = performTokenAcquisition(account!)

    try {
      return await tokenRefreshPromise.value
    } finally {
      tokenRefreshPromise.value = null
    }
  }

  /**
   * Performs token acquisition using MSAL's acquireTokenSilent method.
   *
   * @param account
   */
  async function performTokenAcquisition(account: AccountInfo): Promise<string | null> {
    const tokenRequest = { scopes: apiScopes.value, account, forceRefresh: false }

    try {
      const response = await msalInstance.value.acquireTokenSilent(tokenRequest)
      return response.accessToken
    } catch (error) {
      if (error instanceof Error && error.name === 'InteractionRequiredAuthError') {
        await msalInstance.value.acquireTokenRedirect(tokenRequest)
        return null
      }
      throw error
    }
  }

  /**
   * Loads a dummy user for testing purposes when authentication is not enabled.
   */
  function initializeMock() {
    user.value = {
      initials: 'JJJJ',
      username: 'janedoe@example.com',
      name: 'Jane Doe',
    }
    isAuthenticated.value = true
    isInitialized.value = true
  }

  return {
    user,
    isInitialized,
    isAuthenticated,
    isAuthEnabled,
    initialize,
    initializeMock,
    login,
    acquireToken,
  }
})
